/**
 * B7 (E-2.J.9-02): Email Client Preview Rendering
 *
 * Show how the email looks in different clients.
 * Known rendering quirks per client applied as CSS transforms.
 * At minimum: desktop + mobile + images-off.
 */

export type EmailClient =
  | "gmail_web"
  | "gmail_mobile"
  | "outlook_desktop"
  | "outlook_web"
  | "apple_mail"
  | "samsung_mail";

export type PreviewMode = "desktop" | "mobile" | "images_off";

export interface ClientPreviewConfig {
  client: EmailClient;
  label: string;
  viewport: { width: number; height: number };
  quirks: ClientQuirk[];
}

export interface ClientQuirk {
  description: string;
  cssOverride?: string;
  htmlTransform?: (html: string) => string;
}

/**
 * Known email client rendering configurations.
 * Each client has specific quirks that affect how HTML email renders.
 */
export const CLIENT_CONFIGS: ClientPreviewConfig[] = [
  {
    client: "gmail_web",
    label: "Gmail (Web)",
    viewport: { width: 600, height: 800 },
    quirks: [
      { description: "Strips <style> tags — inline styles only", cssOverride: "" },
      { description: "Prefixes class names with random string" },
      { description: "Wraps content in div with 'im' class" },
    ],
  },
  {
    client: "gmail_mobile",
    label: "Gmail (Mobile)",
    viewport: { width: 375, height: 667 },
    quirks: [
      { description: "Scales to fit screen width" },
      { description: "Dark mode inverts colors on Android" },
    ],
  },
  {
    client: "outlook_desktop",
    label: "Outlook (Desktop)",
    viewport: { width: 600, height: 800 },
    quirks: [
      { description: "Uses Word rendering engine — no border-radius" },
      { description: "VML required for rounded buttons" },
      { description: "Max 1800px image width" },
      { description: "No CSS background images" },
      {
        description: "Strips margin on paragraphs",
        cssOverride: "p { mso-margin-top-alt: 0; mso-margin-bottom-alt: 0; }",
      },
    ],
  },
  {
    client: "outlook_web",
    label: "Outlook (Web)",
    viewport: { width: 600, height: 800 },
    quirks: [
      { description: "Better CSS support than desktop" },
      { description: "Strips class-based styles" },
    ],
  },
  {
    client: "apple_mail",
    label: "Apple Mail",
    viewport: { width: 600, height: 800 },
    quirks: [
      { description: "Best CSS support — renders most things correctly" },
      { description: "Dark mode uses color-scheme: dark" },
      { description: "Supports media queries fully" },
    ],
  },
  {
    client: "samsung_mail",
    label: "Samsung Mail",
    viewport: { width: 375, height: 667 },
    quirks: [
      { description: "Limited media query support" },
      { description: "May enlarge small text" },
    ],
  },
];

/**
 * Apply client-specific quirks to generate a preview-adjusted HTML.
 * This is an approximation — true rendering requires actual email clients.
 */
export function applyClientQuirks(
  html: string,
  client: EmailClient,
): string {
  const config = CLIENT_CONFIGS.find((c) => c.client === client);
  if (!config) return html;

  let result = html;
  for (const quirk of config.quirks) {
    if (quirk.htmlTransform) {
      result = quirk.htmlTransform(result);
    }
  }

  return result;
}

/**
 * Simulate "images off" mode — replace images with alt text placeholders.
 */
export function simulateImagesOff(html: string): string {
  return html.replace(
    /<img[^>]*alt="([^"]*)"[^>]*>/gi,
    (_match, alt: string) =>
      `<div style="background:#e0e0e0;padding:20px;text-align:center;color:#666;font-size:14px;border:1px dashed #ccc;">${alt || "[Image]"}</div>`,
  );
}

/**
 * Get preview viewport dimensions for a mode.
 */
export function getPreviewViewport(mode: PreviewMode): { width: number; height: number } {
  switch (mode) {
    case "desktop": return { width: 600, height: 800 };
    case "mobile": return { width: 375, height: 667 };
    case "images_off": return { width: 600, height: 800 };
  }
}
